import { Agent } from "@biblioteksentralen/cordata";
import {
  Box,
  ChakraProps,
  Grid,
  HStack,
  Heading,
  Image,
  Link,
  Stack,
  Text,
  Tooltip,
  VStack,
  colors,
} from "@biblioteksentralen/react";
import { isNonNil } from "@libry-content/common";
import { unique } from "radash";
import { User } from "react-feather";
import { CommonLinkStyling } from "../../../components/blockContent/LinkSerializer";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { useSitePalette } from "../../../utils/useSitePalette";
import { formatAgentName, getAgentTitle } from "../../cordata/agents";
import { getAgentDatesString } from "../../cordata/contributors";
import { ExternalAgentData } from "../../externalDataFetchers/fetchExternalAgentData";
import { WikidataImageInfo } from "../../externalDataFetchers/wikidataImageInfo";

type Props = {
  agent: Agent;
  externalData: ExternalAgentData | null;
};

export const AgentViewBanner = ({ agent, externalData }: Props) => {
  const { t, ts } = useTranslation();
  const palette = useSitePalette();
  const name = formatAgentName(ts, agent);
  const title = getAgentTitle(ts, agent);
  const yearsString = getAgentDatesString(agent, t);
  const nationalities = unique(agent.nationality?.map(({ demonym }) => demonym)?.filter(isNonNil)).flatMap(
    (nationality, index, genresAndAudiences) => (genresAndAudiences.length - 1 !== index ? [nationality] : nationality)
  );
  const { wikidataImage, snl, wikidata } = externalData ?? {};

  return (
    <Box
      display="grid"
      gridTemplateRows={externalData?.snl || externalData?.wikidata ? "auto 1fr" : "auto"}
      gridTemplateColumns={{
        base: `1fr 6rem`,
        sm: `8rem 1fr`,
        md: `12rem 1fr`,
      }}
      gridTemplateAreas={{
        base: `"header image"
                "text text" `,
        sm: `"image header"
            "image text"`,
      }}
      alignItems="flex-start"
      gap={{ base: "0.5rem 1rem", sm: "0.5rem 2rem" }}
      width="100%"
      padding={{ base: "1rem", sm: "2rem", lg: "3rem" }}
      margin="0 auto"
      borderRadius="2xl"
      position="relative"
      {...palette.colors.card.css}
    >
      <Box gridArea="image">
        {wikidataImage?.thumburl && (
          <Box fontStyle="italic" textAlign="center" fontSize={{ base: "xs", md: "sm" }}>
            <Image
              src={wikidataImage.thumburl}
              alt={ts(agent.name)}
              aspectRatio={1}
              width="100%"
              borderRadius="50%"
              objectFit="cover"
            />
            <Box marginTop="0.25rem">{<ImageCredits image={wikidataImage}></ImageCredits>}</Box>
          </Box>
        )}

        {!wikidataImage?.thumburl && (
          <Grid
            width="100%"
            height="100%"
            padding="1.5rem"
            aspectRatio={1}
            borderRadius="50%"
            placeItems="center"
            {...palette.colors.darkaccent1.css}
          >
            <User strokeWidth={1.75} width="75%" height="75%" />
          </Grid>
        )}
      </Box>
      <VStack display="flex" gap="0" alignSelf="center" alignItems="flex-start">
        <Heading fontSize={{ base: "2xl", sm: "3xl", md: "4xl" }} gridArea="header" as="h1">
          {name}
        </Heading>
        {!!title && <Text _firstLetter={{ textTransform: "uppercase" }}>{title}</Text>}
        <HStack
          flexWrap="wrap"
          columnGap="2"
          rowGap="0"
          spacing="0"
          // eslint-disable-next-line i18next/no-literal-string
          divider={<Text aria-hidden>•</Text>}
          fontSize={{ base: "sm", sm: "md" }}
        >
          {yearsString && <Text>{yearsString}</Text>}
          {nationalities.map((text, index) => (
            <Text key={index}>{text}</Text>
          ))}
        </HStack>
      </VStack>
      <Stack spacing="0.5rem" gridArea="text" fontSize={{ base: "sm", sm: "md" }}>
        {snl?.first_sentences && (
          <>
            <Text marginTop="0.25rem">{snl.first_sentences}</Text>
            <Text fontStyle="italic">
              {t("Hentet fra")}{" "}
              <CommonLinkStyling href={snl.article_url} accentColor={colors.black} hoverColor={colors.grey60}>
                {t("Store norske leksikon")}
              </CommonLinkStyling>
            </Text>
          </>
        )}
        {!snl?.first_sentences && !!wikidata?.description && (
          <>
            <Text marginTop="0.25rem" _firstLetter={{ textTransform: "capitalize" }}>
              {wikidata?.description}
            </Text>
            <Text as="span" fontStyle="italic">
              {t("Hentet fra")}{" "}
              <CommonLinkStyling href={wikidata.url} accentColor={colors.black} hoverColor={colors.grey60}>
                {"Wikidata"}
              </CommonLinkStyling>
            </Text>
          </>
        )}
      </Stack>
    </Box>
  );
};

const ImageLicenseLink = ({ url, licenseName }: { url: string; licenseName: string } & ChakraProps) => {
  return (
    <Link marginLeft="0.2rem" fontSize="xs" href={url}>
      ({licenseName})
    </Link>
  );
};

const ImageCredits = ({ image }: { image: WikidataImageInfo }) => {
  const { t } = useTranslation();
  const fotoText = t("Foto");
  const { artist, licenseShortName, licenseUrl } = image;

  if (licenseShortName == "Public domain") {
    if (licenseUrl) return <ImageLicenseLink url={licenseUrl} licenseName={t("Falt i det fri")} />;
    else
      return (
        <Text>
          {fotoText}: {t("Falt i det fri")}
        </Text>
      );
  }

  if (artist)
    return (
      <Box>
        <Tooltip label={artist}>
          <Text
            sx={{ WebkitBoxOrient: "vertical", WebkitLineClamp: 1 }}
            overflow="hidden"
            textOverflow="ellipsis"
            maxWidth="100%"
            display="-webkit-box"
          >
            {fotoText}: {artist}
          </Text>
        </Tooltip>

        {licenseUrl && licenseShortName && <ImageLicenseLink url={licenseUrl} licenseName={licenseShortName} />}
      </Box>
    );
  // Fallback dersom wikidata ikke gir ut noe relevant info
  else
    return (
      <Text fontStyle="italic">
        {fotoText}: {"Wikidata"}
      </Text>
    );
};
